import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@shared-kernel/primary/shared/shadcn/ui/input-group";
import { normalizeSocialUrlInput, normalizeUrl, stripUrlProtocol } from "./url-input.helpers";

type UrlKind = "website" | "social";

interface Props extends Omit<ComponentPropsWithoutRef<typeof InputGroupInput>, "value" | "onChange"> {
  /** Leading addon icon, e.g. <Globe />. */
  icon: ReactNode;
  /** Leading addon label, e.g. "Site perso". Kept short to fit the fixed-width addon. */
  label: ReactNode;
  /** Raw stored value (scheme included); rendered without its scheme. */
  value?: string | null;
  /** Receives the normalized value (scheme re-added). Compatible with RHF field.onChange. */
  onChange: (value: string) => void;
  /** "website" keeps an existing http(s) scheme; "social" forces https. Defaults to "website". */
  kind?: UrlKind;
}

/**
 * URL text input with a fixed-width leading addon (icon + label) and a
 * protocol-free preview. Stacking several with the same label width keeps their
 * inputs vertically aligned. Validation (valid URL, domain rules) stays in the
 * form schema — this only handles display and input-time normalization.
 */
export const UrlInput = forwardRef<HTMLInputElement, Props>(function UrlInput(
  { icon, label, value, onChange, kind = "website", ...inputProps },
  ref,
) {
  const normalize = kind === "social" ? normalizeSocialUrlInput : normalizeUrl;
  return (
    <InputGroup>
      <InputGroupAddon className="mr-2 w-32 justify-start border-r border-input pr-3">
        {icon}
        <span>{label}</span>
      </InputGroupAddon>
      <InputGroupInput
        {...inputProps}
        ref={ref}
        value={stripUrlProtocol(value ?? "")}
        onChange={event => onChange(normalize(event.target.value))}
      />
    </InputGroup>
  );
});
